import React, { useState } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  searchDictionary,
  type DictionaryItem,
} from '../../services/apiService';

interface QuickTestBoxProps {
  /** Only published answers are eligible for the live-rep search. */
  liveAnswerCount: number;
}

/** Search the published Algolia index. Unpublished draft testing lives only in
 * the onboarding review's whole-draft DraftKnowledgeSearch. */
const QuickTestBox: React.FC<QuickTestBoxProps> = ({ liveAnswerCount }) => {
  const [liveQuery, setLiveQuery] = useState('');
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveSearched, setLiveSearched] = useState(false);
  const [liveResults, setLiveResults] = useState<DictionaryItem[]>([]);
  const [liveError, setLiveError] = useState<string | null>(null);

  const handleLiveTest = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = liveQuery.trim();
    if (trimmed.length < 2) {
      setLiveError('Type at least a couple of words to test.');
      return;
    }
    setLiveLoading(true);
    setLiveError(null);
    try {
      const response = await searchDictionary(trimmed, 'all', 1, 3);
      setLiveResults(response.results || []);
      setLiveSearched(true);
    } catch {
      setLiveError('Could not test that right now — try again in a moment.');
    } finally {
      setLiveLoading(false);
    }
  };

  const unavailableLiveTest = (
    <div className="rounded-xl border border-hairline bg-surface-subtle p-4">
      <h2 className="text-sm font-semibold text-ink">Search objections and FAQs in the KB</h2>
      <div className="mt-3 flex gap-2">
        <input aria-label="Published-answer search unavailable" disabled placeholder="Publish starter answers to enable this search" className="min-w-0 flex-1 rounded-lg border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted" />
        <button type="button" disabled className="kb-button kb-button-secondary shrink-0 px-4 py-2 text-sm"><SearchIcon style={{ fontSize: '1.1rem' }} />Search</button>
      </div>
    </div>
  );

  if (liveAnswerCount === 0) {
    return unavailableLiveTest;
  }

  return (
    <div className="kb-quick-test space-y-3">
      <div className="kb-quick-test-inner rounded-xl border border-hairline bg-surface p-4">
        <h2 className="text-sm font-semibold text-ink">Search objections and FAQs in the KB</h2>
        <p className="mt-0.5 mb-3 text-xs text-ink-muted">
          Shows what's published. Use it to check content.
        </p>
        <form onSubmit={handleLiveTest} className="flex gap-2" aria-label="Search objections and FAQs in the KB">
          <input aria-label="Search published questions and answers" type="text" value={liveQuery} onChange={(event) => setLiveQuery(event.target.value)} placeholder="e.g. Is this too expensive?" className="min-w-0 flex-1 rounded-lg border border-ink-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-signature" />
          <button type="submit" disabled={liveLoading} className="kb-button kb-button-secondary flex shrink-0 items-center gap-1.5 px-4 py-2 text-sm font-medium disabled:opacity-50"><SearchIcon style={{ fontSize: '1.1rem' }} />{liveLoading ? 'Searching…' : 'Search'}</button>
        </form>
        {liveError && <p role="alert" className="mt-2 text-xs text-error">{liveError}</p>}
        {liveSearched && !liveError && (
          <div className="mt-3 space-y-2" role="status" aria-live="polite">
            {liveResults.length === 0 ? <p className="text-sm text-ink-muted">No close live match found. Consider adding this as a new answer.</p> : liveResults.map((result) => (
              <div key={result._id} className="rounded-lg border border-hairline bg-surface-subtle p-3">
                <span className="rounded-full border border-hairline bg-surface px-2 py-0.5 text-xs text-ink-secondary">{result.type === 'faq' ? 'FAQ' : 'Objection'} · published</span>
                <p className="mt-2 text-sm font-medium text-ink">{result.question}</p>
                <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-ink-secondary">{result.answer}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickTestBox;
